import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconCircleCheck, IconClock, IconTruckDelivery } from '@tabler/icons-react';
import { useConfirmShipmentReady } from '@/hooks/useDeals';
import type { BargainStepsInfo } from '@/types/deals';

interface ShipmentConfirmationStepProps {
  readonly dealId: number;
  readonly info: BargainStepsInfo;
}

// Шаг «Подтверждение отгрузки» (id 5). Продавец подтверждает готовность товара к отгрузке
// (+ получение денег, если есть предоплата). Покупатель/агент — ожидают.
export default function ShipmentConfirmationStep({ dealId, info }: ShipmentConfirmationStepProps) {
  const { t } = useTranslation();
  const completed = info.processingStep === 'SHIPMENT_READY_CONFIRMED';
  const isSeller = info.isSeller === true;
  // Форма подтверждения активна только на самой стадии подтверждения отгрузки. Продавец попадает сюда
  // и раньше — на шагах подписи спецификации (для него это уже стадия «Подтверждение отгрузки»,
  // см. DealStepPanel); там он ещё ничего не подтверждает, а ожидает.
  const shipmentStageActive = info.processingStep === 'WAIT_SHIPMENT_IS_READY_CONFIRMATION';
  const hasPrepayment = (info.prepaymentPercent ?? 0) > 0;

  const [moneyConfirmed, setMoneyConfirmed] = useState(false);
  const [shipmentReady, setShipmentReady] = useState(false);
  const confirm = useConfirmShipmentReady(dealId);
  const canSubmit = shipmentReady && (!hasPrepayment || moneyConfirmed) && !confirm.isPending;

  // ── Завершено ────────────────────────────────────────────────────────────────
  if (completed) {
    return (
      <Stack gap="sm">
        <Group gap="xs" wrap="nowrap">
          <ThemeIcon size={32} radius="xl" variant="light" color="lime">
            <IconCircleCheck size={18} />
          </ThemeIcon>
          <Text fz="sm" fw={500}>
            {info.shipmentReadyDate ?? t('app.deals.shipmentReady.done')}
          </Text>
        </Group>
        <Stack gap={4} pl={40}>
          {hasPrepayment ? (
            <Text fz="sm" c="gray.8">
              ✓ {t('app.deals.shipmentReady.confirmedMoney')}
            </Text>
          ) : null}
          <Text fz="sm" c="gray.8">
            ✓ {t('app.deals.shipmentReady.confirmedReady')}
          </Text>
        </Stack>
      </Stack>
    );
  }

  // ── Продавец ждёт (шаги до активации подтверждения, напр. подпись спецификации) ──
  if (isSeller && !shipmentStageActive) {
    return (
      <Group gap="xs" wrap="nowrap">
        <ThemeIcon size={32} radius="xl" variant="light" color="brand">
          <IconClock size={18} />
        </ThemeIcon>
        <Text fz="sm">{t('app.deals.shipmentReady.sellerWaiting')}</Text>
      </Group>
    );
  }

  // ── Продавец — форма подтверждения ────────────────────────────────────────────
  if (isSeller) {
    return (
      <Stack gap="md">
        <Text fz="sm" c="gray.8">
          {t('app.deals.shipmentReady.description')}
        </Text>
        <Stack gap="xs">
          {hasPrepayment ? (
            <Checkbox
              checked={moneyConfirmed}
              onChange={(event) => setMoneyConfirmed(event.currentTarget.checked)}
              label={t('app.deals.shipmentReady.money')}
              disabled={confirm.isPending}
            />
          ) : null}
          <Checkbox
            checked={shipmentReady}
            onChange={(event) => setShipmentReady(event.currentTarget.checked)}
            label={t('app.deals.shipmentReady.ready')}
            disabled={confirm.isPending}
          />
        </Stack>
        <Button
          leftSection={<IconTruckDelivery size={16} />}
          disabled={!canSubmit}
          loading={confirm.isPending}
          onClick={() => confirm.mutate()}
          w="fit-content"
        >
          {t('app.deals.shipmentReady.confirm')}
        </Button>
      </Stack>
    );
  }

  // ── Покупатель / Агент — ожидание ─────────────────────────────────────────────
  return (
    <Group gap="xs" wrap="nowrap">
      <ThemeIcon size={32} radius="xl" variant="light" color="brand">
        <IconClock size={18} />
      </ThemeIcon>
      <Text fz="sm">{t('app.deals.shipmentReady.waiting')}</Text>
    </Group>
  );
}
